import React, { useState, useEffect, useCallback } from 'react';
import { Card, Table, Tag, Button, Space, Select, Typography, Collapse, Image, message, Modal, Input, Tabs } from 'antd';
import { BugOutlined, ReloadOutlined, QuestionCircleOutlined, SendOutlined, HistoryOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { bugReportApi } from '../../../services/bugReportApi';
import { sendMessage, fetchUsers } from '../../Inbox/services';
import type { UserInfo } from '../../Inbox/models';
import type { BugReport, BugStats, Annotation } from '../../../components/BugReport/types';
import BugFixHistoryPanel from './BugFixHistoryPanel';

const { Text } = Typography;

const STATUS_CONFIG: Record<string, { color: string; label: string }> = {
  open: { color: 'red', label: 'Open' },
  in_progress: { color: 'orange', label: 'In Progress' },
  resolved: { color: 'green', label: 'Resolved' },
  closed: { color: 'default', label: 'Closed' },
};

const BugReportPanel: React.FC = () => {
  const [bugs, setBugs] = useState<BugReport[]>([]);
  const [stats, setStats] = useState<BugStats | null>(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [page, setPage] = useState(1);
  const [users, setUsers] = useState<UserInfo[]>([]);
  const [askBug, setAskBug] = useState<BugReport | null>(null);
  const [askContent, setAskContent] = useState('');
  const [askSending, setAskSending] = useState(false);

  const openAsk = (bug: BugReport) => {
    const descriptions = (bug.annotations || []).map((a, i) => `#${i + 1} ${a.description}`).join('\n');
    setAskContent(`關於您描述的問題：\n${descriptions}\n\n`);
    setAskBug(bug);
  };

  const handleAskSend = async () => {
    if (!askBug) return;
    const reporter = askBug.reportedBy;
    const matched = users.find(u => u.userName === reporter || u.userId === reporter);
    const toUserId = matched?.userId || reporter;

    setAskSending(true);
    try {
      await sendMessage({
        toUserId,
        subject: `#BUG${askBug.id} 管理者想知道更多細節`,
        content: askContent,
      });
      message.success(`已發送詢問給 ${matched?.userName || reporter}`);
      setAskBug(null);
      setAskContent('');
    } catch {
      message.error('發送失敗');
    } finally {
      setAskSending(false);
    }
  };

  const fetchBugs = useCallback(async () => {
    setLoading(true);
    try {
      const [bugsRes, statsRes] = await Promise.all([
        bugReportApi.list({ page, size: 10, status: statusFilter || undefined }),
        bugReportApi.getStats(),
      ]);
      setBugs(bugsRes.data);
      setTotal(bugsRes.total);
      setStats(statsRes);
    } catch (err) {
      console.error('Fetch bugs error:', err);
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter]);

  useEffect(() => {
    fetchBugs();
  }, [fetchBugs]);

  useEffect(() => {
    fetchUsers().then(setUsers).catch(() => {});
  }, []);

  const handleStatusChange = async (bug: BugReport, newStatus: string) => {
    try {
      await bugReportApi.update(bug.id, { status: newStatus, resolvedBy: 'dashboard' });

      // When resolving, auto-send fix notification to reporter
      if (newStatus === 'resolved') {
        const reporter = bug.reportedBy;
        const matched = users.find(u => u.userName === reporter || u.userId === reporter);
        const toUserId = matched?.userId || reporter;
        const reportDate = bug.createdAt ? new Date(bug.createdAt).toLocaleDateString('zh-TW') : '未知日期';
        const descriptions = (bug.annotations || []).map((a, i) => `#${i + 1} ${a.description}`).join('\n');

        sendMessage({
          toUserId,
          subject: `(#${bug.id})BUG完成修復回報`,
          content: `您於 ${reportDate} 回報的BUG#${bug.id} 模組(${bug.formId || 'unknown'})\n${descriptions}\n\n已經修復完成，再麻煩幫我測試，如果還是有問題，可以直接回覆本封信件，謝謝!`,
        }).catch(() => {}); // fire and forget
      }

      message.success(`Bug #${bug.id} updated to ${newStatus}`);
      fetchBugs();
    } catch (err) {
      message.error('Update failed');
    }
  };

  const columns: ColumnsType<BugReport> = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 60,
      render: (id: number) => <Text strong>#{id}</Text>,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 110,
      render: (status: string) => {
        const cfg = STATUS_CONFIG[status] || { color: 'default', label: status };
        return <Tag color={cfg.color}>{cfg.label}</Tag>;
      },
    },
    {
      title: 'FormId',
      dataIndex: 'formId',
      key: 'formId',
      width: 120,
      render: (formId: string | null) => formId || <Text type="secondary">--</Text>,
    },
    {
      title: 'Page',
      dataIndex: 'page',
      key: 'page',
      width: 150,
      ellipsis: true,
    },
    {
      title: 'Annotations',
      dataIndex: 'annotations',
      key: 'annotations',
      width: 80,
      render: (annotations: Annotation[]) => (
        <Tag>{annotations?.length || 0}</Tag>
      ),
    },
    {
      title: 'Reporter',
      dataIndex: 'reportedBy',
      key: 'reportedBy',
      width: 100,
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 140,
      render: (val: string) => val ? new Date(val).toLocaleString('zh-TW') : '--',
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 180,
      render: (_: unknown, record: BugReport) => (
        <Space size="small">
          {record.status === 'open' && (
            <Button size="small" onClick={() => handleStatusChange(record, 'in_progress')}>
              Start
            </Button>
          )}
          {(record.status === 'open' || record.status === 'in_progress') && (
            <Button size="small" type="primary" onClick={() => handleStatusChange(record, 'resolved')}>
              Resolve
            </Button>
          )}
          {record.status === 'resolved' && (
            <Button size="small" onClick={() => handleStatusChange(record, 'closed')}>
              Close
            </Button>
          )}
        </Space>
      ),
    },
  ];

  const expandedRowRender = (record: BugReport) => {
    const annotations = record.annotations || [];
    const screenshots = record.screenshots || [];
    return (
      <div>
        <Collapse
          size="small"
          items={annotations.map((a, i) => ({
            key: i,
            label: (
              <span>
                <Tag color="red">#{i + 1}</Tag>
                {a.label && <Tag color="blue">{a.label}</Tag>}
                <code style={{ fontSize: 11, color: '#8c8c8c' }}>{a.selector}</code>
              </span>
            ),
            children: (
              <div>
                <p>{a.description}</p>
                <Text type="secondary" style={{ fontSize: 11 }}>
                  Element: &lt;{a.elementTag}&gt; | Text: "{a.elementText}"
                </Text>
              </div>
            ),
          }))}
        />
        {screenshots.length > 0 && (
          <div style={{ marginTop: 12 }}>
            <Text strong style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>
              Screenshots ({screenshots.length})
            </Text>
            <Image.PreviewGroup>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {screenshots.map((url, idx) => (
                  <Image
                    key={idx}
                    src={url}
                    width={120}
                    height={80}
                    style={{ objectFit: 'cover', borderRadius: 4, border: '1px solid #d9d9d9' }}
                  />
                ))}
              </div>
            </Image.PreviewGroup>
          </div>
        )}
        <div style={{ marginTop: 12 }}>
          <Button
            icon={<QuestionCircleOutlined />}
            onClick={() => openAsk(record)}
            size="small"
            type="dashed"
          >
            詢問回報者
          </Button>
        </div>
      </div>
    );
  };

  return (
    <>
      <Tabs
        defaultActiveKey="list"
        items={[
          {
            key: 'list',
            label: <span><BugOutlined /> 回報列表</span>,
            children: (
              <Card
                title={
                  <Space>
                    <BugOutlined style={{ color: '#ff4d4f' }} />
                    <span>Bug Reports</span>
                    {stats && (
                      <Space size="small">
                        <Tag color="red">{stats.open} open</Tag>
                        <Tag color="orange">{stats.inProgress} in progress</Tag>
                        <Tag color="green">{stats.resolved} resolved</Tag>
                      </Space>
                    )}
                  </Space>
                }
                extra={
                  <Space>
                    <Select
                      value={statusFilter}
                      onChange={val => { setStatusFilter(val); setPage(1); }}
                      style={{ width: 140 }}
                      options={[
                        { label: 'All', value: '' },
                        { label: 'Open', value: 'open' },
                        { label: 'In Progress', value: 'in_progress' },
                        { label: 'Resolved', value: 'resolved' },
                        { label: 'Closed', value: 'closed' },
                      ]}
                    />
                    <Button icon={<ReloadOutlined />} onClick={fetchBugs} loading={loading}>
                      Refresh
                    </Button>
                  </Space>
                }
              >
                <Table
                  columns={columns}
                  dataSource={bugs}
                  rowKey="id"
                  loading={loading}
                  size="small"
                  expandable={{ expandedRowRender }}
                  pagination={{
                    current: page,
                    pageSize: 10,
                    total,
                    onChange: p => setPage(p),
                    showSizeChanger: false,
                    showTotal: t => `Total: ${t}`,
                  }}
                />
              </Card>
            ),
          },
          {
            key: 'history',
            label: <span><HistoryOutlined /> 歷史紀錄</span>,
            children: <BugFixHistoryPanel />,
          },
        ]}
      />
      <Modal
        title={askBug ? `#BUG${askBug.id} 詢問回報者 (${askBug.reportedBy})` : ''}
        open={!!askBug}
        onCancel={() => { setAskBug(null); setAskContent(''); }}
        footer={[
          <Button key="cancel" onClick={() => { setAskBug(null); setAskContent(''); }}>取消</Button>,
          <Button key="send" type="primary" icon={<SendOutlined />} loading={askSending} onClick={handleAskSend}>
            發送詢問
          </Button>,
        ]}
        width={560}
      >
        <div style={{ marginBottom: 8 }}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            主旨：#BUG{askBug?.id} 管理者想知道更多細節
          </Text>
        </div>
        <div style={{ marginBottom: 8 }}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            收件人：{askBug?.reportedBy}
          </Text>
        </div>
        <Input.TextArea
          value={askContent}
          onChange={e => setAskContent(e.target.value)}
          rows={8}
          placeholder="輸入要詢問的內容..."
        />
      </Modal>
    </>
  );
};

export default BugReportPanel;
